import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => {
      data.append(key, value as string);
    });

    try {
      await fetch('https://readdy.ai/api/form/d7foubuivmjfhtdrg4e0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#1a2e4a]">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/40 mb-4">Contact</p>
          <h1 className="text-4xl md:text-5xl font-light text-white leading-tight tracking-tight mb-4">
            Parlons de<br />
            <span className="font-semibold">votre projet</span>
          </h1>
          <p className="text-sm text-white/60 max-w-lg leading-relaxed">
            Contactez-nous pour toute demande. Nous vous répondrons dans les meilleurs délais.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">

            {/* Info */}
            <div className="lg:col-span-2 flex flex-col gap-8">
              <div>
                <h2 className="text-lg font-semibold text-[#1a2e4a] mb-1">BONIEK</h2>
                <p className="text-sm text-gray-400">Dominika Julia Boniek</p>
                <p className="text-xs text-gray-400 mt-1">SIRET : 102 005 980 00010</p>
              </div>

              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-[#1a2e4a]/8 shrink-0">
                    <i className="ri-map-pin-line text-[#1a2e4a] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Adresse</p>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      27 Rue Malbec<br />
                      33800 Bordeaux<br />
                      France
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-[#1a2e4a]/8 shrink-0">
                    <i className="ri-phone-line text-[#1a2e4a] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Téléphone</p>
                    <a
                      href="tel:+33751108791"
                      className="text-sm text-gray-600 hover:text-[#1a2e4a] transition-colors cursor-pointer"
                    >
                      +33 7 51 10 87 91
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-[#1a2e4a]/8 shrink-0">
                    <i className="ri-mail-line text-[#1a2e4a] text-lg"></i>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">Email</p>
                    <a
                      href="mailto:dom1n1kaboniek@outlook.com"
                      className="text-sm text-gray-600 hover:text-[#1a2e4a] transition-colors cursor-pointer break-all"
                    >
                      dom1n1kaboniek@outlook.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-lg overflow-hidden border border-gray-100 h-48">
                <iframe
                  title="BONIEK Bordeaux"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2831.0!2d-0.5792!3d44.8378!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd5527e8f751ca81%3A0x796386037b1ce71!2s27%20Rue%20Malbec%2C%2033800%20Bordeaux!5e0!3m2!1sfr!2sfr!4v1700000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#1a2e4a]/10 mb-6">
                    <i className="ri-check-line text-[#1a2e4a] text-2xl"></i>
                  </div>
                  <h3 className="text-xl font-semibold text-[#1a2e4a] mb-3">Message envoyé</h3>
                  <p className="text-sm text-gray-500 max-w-sm leading-relaxed">
                    Merci pour votre message. Nous vous répondrons dans les meilleurs délais.
                  </p>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="contact-boniek"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                        Nom <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="nom"
                        required
                        placeholder="Votre nom"
                        className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a] transition-colors bg-[#fafafa]"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                        Email <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        placeholder="votre@email.com"
                        className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a] transition-colors bg-[#fafafa]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                      Téléphone
                    </label>
                    <input
                      type="tel"
                      name="telephone"
                      placeholder="+33 6 00 00 00 00"
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a] transition-colors bg-[#fafafa]"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">
                      Description de la demande <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      placeholder="Décrivez votre projet ou votre demande..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-md border border-gray-200 text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-[#1a2e4a] transition-colors bg-[#fafafa] resize-none"
                    ></textarea>
                    <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-gray-400'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="whitespace-nowrap w-full sm:w-auto px-8 py-3.5 rounded-md bg-[#1a2e4a] text-white text-sm font-semibold hover:bg-[#243d61] transition-colors duration-200 cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Envoyer
                        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-line"></i></span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
